import dayjs from 'dayjs';

import { DATE_KEY_FORMAT } from '@/helpers';

import { AssignmentType, RowItem, RowItemType, TimeOffBlockType } from '../types';
import { PlannerMemberResponse } from '../types/dto';
import { mapAssignmentsFromPlannerResponse } from './assignment.mapper';
import { mapTimeOffsFromPlannerResponse } from './time-off.mapper';

/**
 * Adapter to convert PlannerMemberResponse[] from API to the format
 * expected by getPlannerDataByUsers utility function.
 *
 * Transforms API response structure to match the planner UI requirements.
 */
export const adaptPlannerMembersToUserData = (
  members: PlannerMemberResponse[] | undefined
): {
  rows: RowItem[];
  assignments: Map<string, AssignmentType[]>;
  timeOffs: Map<string, TimeOffBlockType[]>;
} => {
  if (!members || members.length === 0) {
    return { rows: [], assignments: new Map(), timeOffs: new Map() };
  }

  const result = members.reduce(
    (acc, member) => {
      const userId = String(member.user.id);
      const currentState = member.stateChanges[0];
      const hoursPerDay = currentState?.workingMinutesPerDay
        ? currentState.workingMinutesPerDay / 60
        : 8;

      // Convert utilization array to map for member (extract date part as key)
      // Fix percent when backend returns 0% for members with 0 capacity but assigned work
      const memberUtilizationMap = new Map(
        member.utilizationByDate.map(util => [
          dayjs(util.date).format(DATE_KEY_FORMAT),
          util.capacity === 0 ? { ...util, percent: 100 } : util,
        ])
      );

      // Add user row
      acc.rows.push({
        id: userId,
        title: member.user.name,
        user: member.user,
        hoursPerDay,
        type: RowItemType.User,
        stateChanges: member.stateChanges,
        customAttributes: member.customAttributes ?? [],
        utilizationByDate: memberUtilizationMap,
      });

      // Map assignments for this user
      const mappedAssignments = mapAssignmentsFromPlannerResponse(member.assignments);
      acc.assignments.set(userId, mappedAssignments);

      // Map time-offs for this user
      const mappedTimeOffs = mapTimeOffsFromPlannerResponse(member.timeOffs);
      acc.timeOffs.set(userId, mappedTimeOffs);

      return acc;
    },
    { rows: [], assignments: new Map(), timeOffs: new Map() } as {
      rows: RowItem[];
      assignments: Map<string, AssignmentType[]>;
      timeOffs: Map<string, TimeOffBlockType[]>;
    }
  );

  // Rows must always render A→Z by member name. The select layer merges pages in
  // insertion order, which breaks alphabetical order whenever a HORIZONTAL date-range
  // page introduces members absent from the initial pages (they'd append at the
  // bottom / prepend at the top). Sorting here mirrors what the department/project
  // mappers already do for members within each group — rows and the assignments map
  // are returned together from this adapter, so they stay consistent.
  result.rows.sort((a, b) =>
    a.title.trim().toLocaleLowerCase().localeCompare(b.title.trim().toLocaleLowerCase())
  );

  return result;
};
